import { faPen as regPen, faTrash as regTrash } from '@fortawesome/pro-regular-svg-icons';
import {
    faPen as solidPen,
    faTrash as solidTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionMenu } from 'components/menu/ActionMenu';
import { ActionMenuButton } from 'components/menu/ActionMenuButton';
import { ActionMenuItem } from 'components/menu/ActionMenuItem';
import { usePopup } from 'custom_hooks/usePopup';
import * as React from 'react';
import { ReactNode, useCallback, useRef } from 'react';
import { tokens } from 'styles/tokens';

export type CRUDTableActionsProps<TData> = Readonly<{
    /**
     * Renders a custom action item column
     */
    customActionItems?: (record: TData) => ReactNode;
    /**
     * Callback for when the delete button is clicked
     */
    onDelete?: (record: TData) => void;
    /**
     * Callback for when the edit pencil is clicked
     * If not supplied edit is hidden
     */
    onEdit?: (record: TData) => void;
    /**
     * description of data row to be used in the action button title text.
     * Example: `titleDescription="Corporation"` will result in
     * "Edit Corporation"/"Delete Corporation" when hovering over those action buttons.
     */
    titleDescription?: string;
    /**
     * data provided for one row in the table
     */
    rowData: TData;
}>;

export function CRUDTableActions<TData>({
    customActionItems,
    onDelete,
    onEdit,
    titleDescription = '',
    rowData,
}: CRUDTableActionsProps<TData>) {
    const wrapperRef = useRef<HTMLSpanElement>(null);
    const { close, popupRef, triggerRef, isOpen, toggleOpen } = usePopup();

    const onMenuClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
        (event) => {
            event.stopPropagation();
            toggleOpen();
        },
        [toggleOpen],
    );

    return (
        <span className="pl-10" ref={wrapperRef}>
            <ActionMenuButton className="p-0" onClick={onMenuClick} ref={triggerRef} />
            {isOpen && (
                <ActionMenu
                    useOverlay
                    popupClassName="modal-popup"
                    onClose={close}
                    ref={popupRef}
                    buttonRef={triggerRef}
                    anchor={wrapperRef.current}
                >
                    {customActionItems && customActionItems(rowData)}
                    {onEdit && (
                        <ActionMenuItem
                            title={`Edit ${titleDescription}`}
                            hoverIcon={
                                <FontAwesomeIcon
                                    color={tokens.text.link}
                                    icon={solidPen}
                                />
                            }
                            icon={
                                <FontAwesomeIcon
                                    color={tokens.icon.secondary}
                                    icon={regPen}
                                />
                            }
                            onSelect={() => {
                                close();
                                onEdit(rowData);
                            }}
                        />
                    )}
                    {onDelete && (
                        <ActionMenuItem
                            ariaLabel={`Delete ${titleDescription}`}
                            title={`Delete ${titleDescription}`}
                            hoverIcon={
                                <FontAwesomeIcon
                                    icon={solidTrash}
                                    color={tokens.error.default}
                                />
                            }
                            icon={
                                <FontAwesomeIcon
                                    icon={regTrash}
                                    color={tokens.icon.secondary}
                                />
                            }
                            onSelect={() => {
                                close();
                                onDelete(rowData);
                            }}
                        />
                    )}
                </ActionMenu>
            )}
        </span>
    );
}
